import {Avatar, Card, CardBody, CardHeader, SelectItem} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {TableBody, TableCell, TableColumn, TableHeader, TableRow} from "@heroui/table";
import {Icon} from "@iconify/react";
import {keepPreviousData, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, useParams} from '@tanstack/react-router'
import {useWindowSize} from "@uidotdev/usehooks";
import React, {useEffect, useState} from "react";
import img_female from "../../../assets/img_female.png";
import img_male from "../../../assets/img_male.png";
import Content from "../../../components/Content.tsx";
import PButton from "../../../components/PButton.tsx";
import PInput from "../../../components/PInput.tsx";
import PSelect from "../../../components/PSelect.tsx";
import PTable from "../../../components/PTable.tsx";
import Header from "../../../Header.tsx";
import services from "../../../services";
import {tableFullScreen} from "../../../components/tableStyles.ts";
import queryKeys from "../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/running/results/$import_uid')({
    component: RouteComponent,
})

function RouteComponent() {
    const params = useParams({strict: false});
    const queryClient = useQueryClient()

    const [split, setSplit] = useState('');
    const [filterText, setFilterText] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');

    const runningResultsQuery = useQuery({
        queryKey:        queryKeys.running.getResults(params.import_uid),
        queryFn:         async () => services.running.getResults(params.import_uid!),
        enabled:         !!params.import_uid,
        placeholderData: keepPreviousData,
    })

    useEffect(() => {
        if (runningResultsQuery.data && !split) {
            setSplit(runningResultsQuery.data.splits[0] || '450m');
        }
    }, [runningResultsQuery.data]);

    const size = useWindowSize();
    const isSm = !!size.width && size.width < 1024;

    const columns = (size.width && size.width < 1024
            ? [
                {key: 'combined', title: 'Player'},
            ]
            : [
                {key: 'overallposition', title: 'Position'},
                {key: '_points', title: 'Points'},
                {key: 'name', title: 'Name'},
                {key: 'bib', title: 'bib'},
                {key: 'countrycode', title: 'Country'},
                {key: 'gender', title: 'Gender'},
                {key: 'category', title: 'Category'},
                {key: 'categoryposition', title: 'Category Position'},
                {key: 'split', title: 'Split'},
                {key: 'guntime', title: 'Guntime'},

                // {key: 'elo_rating', title: 'Global ELO'},
                // {key: 'player_sppr_grade', title: 'SPPR'},
                // {key: 'reliability_percent', title: 'Reliability Score'},
                // {key: 'nationality', title: 'Country'},
                // {key: 'status', title: 'Status'},
            ]
    );

    if (!runningResultsQuery.isFetched) {
        return <>
            <Header />

            <Content className="flex justify-center my-6">
                <Spinner size={"lg"} />
            </Content>
        </>
    }

    const runningResultsQueryResults = (runningResultsQuery.data?.results || [])
        .filter((r) => !filterText || r.name.toLowerCase().includes(filterText.toLowerCase()))
        .filter((r) => !categoryFilter || r.category === categoryFilter);

    return (
        <>
            <Header backUrl={"/running"} />
            <Content>

                <div className="w-full flex justify-between items-end mb-4">
                    <div className={"flex items-end"}>
                        <PInput
                            label={"Name:"}
                            labelPlacement={"outside-top"}
                            className={"w-auto mr-2"}
                            onBlur={(e) => setFilterText(e.target.value)}
                        />
                        <PSelect
                            placeholder={"category"}
                            selectedKeys={[categoryFilter]}
                            onSelectionChange={(e) => {
                                setCategoryFilter(e.currentKey as any)
                            }}
                            label={"Category:"}
                            multiple={true}
                            disallowEmptySelection={true}
                        >
                            <SelectItem key={""}></SelectItem>
                            <>
                                {(runningResultsQuery.data?.categories || []).map((category) => <SelectItem key={category}>{category}</SelectItem>)}
                            </>
                        </PSelect>
                        <PButton className={"ml-2"}>Search</PButton>
                    </div>
                    <PButton color="default" onPress={() => queryClient.invalidateQueries({queryKey: queryKeys.running.getResults()})}
                             className={"ml-2"}
                             isIconOnly={isSm}>
                        <Icon icon="material-symbols-light:refresh" className="text-2xl" /> {isSm ? '' : 'refresh'}
                    </PButton>
                </div>
                <Card>
                    <CardHeader className={"flex items-center justify-between"}>
                        <div className={"font-bold text-lg"}>{runningResultsQuery.data?.import.event_title}</div>
                        <div className={"whitespace-nowrap ml-1"}>
                            {runningResultsQuery.data?.results?.length} runners
                        </div>
                    </CardHeader>
                    <CardBody>
                        <PTable removeWrapper={true} classNames={tableFullScreen} isStriped={true} aria-label={"Running Results"}>
                            <TableHeader columns={columns}>
                                {(column) => <TableColumn key={column.key}>
                                    {column.title}
                                    {column.key === 'split' &&
                                        <select onChange={(e) => setSplit(e.target.value as any)}>
                                            {(runningResultsQuery.data?.splits || []).map((split) => <option key={split}>{split}</option>)}
                                        </select>}
                                </TableColumn>}
                            </TableHeader>
                            <TableBody>
                                {runningResultsQueryResults.map((result) => <TableRow key={result.name}>
                                    {(columnKey) => {
                                        if (columnKey === 'combined') {
                                            return <TableCell>
                                                <div className="flex w-full">
                                                    <div className={"flex items-center mr-2"}>
                                                        {result.overallposition}.
                                                    </div>
                                                    <div>
                                                        <Avatar className={"mr-2"} src={(result.gender === 'M' ? img_male : img_female)} />
                                                    </div>
                                                    <div>
                                                        <div className="flex-1 flex items-center">{result.name}</div>
                                                        <small><img className="inline" src={'https://flagcdn.com/16x12/' + result.countrycode?.toLowerCase() + '.png'} alt={result.countrycode + " flag"} title={result.countrycode} /> bib {result.bib}, {result.category}</small>
                                                    </div>
                                                </div>
                                            </TableCell>
                                        }
                                        if (columnKey === 'name') {
                                            return <TableCell>
                                                <div className={"flex w-full items-center"}>
                                                    <Avatar className={"mr-2"} src={img_male} />
                                                    {result.name}
                                                </div>
                                            </TableCell>
                                        }
                                        if (columnKey === 'split') {
                                            return <TableCell>
                                                {result['split|' + split]}
                                            </TableCell>
                                        }
                                        if (columnKey === 'countrycode') {
                                            return <TableCell>
                                                <img className="inline" src={'https://flagcdn.com/16x12/' + result.countrycode?.toLowerCase() + '.png'} alt={result.countrycode + " flag"} title={result.countrycode} />
                                            </TableCell>
                                        }
                                        if (columnKey === 'gender') {
                                            return <TableCell>
                                                {result.gender === 'M' ? 'Male' : 'Female'}
                                            </TableCell>
                                        }
                                        if (result.hasOwnProperty(columnKey)) {
                                            return <TableCell>
                                                {result[columnKey] || '-'}
                                            </TableCell>
                                        }
                                        return (
                                            <TableCell>
                                                {columnKey}
                                            </TableCell>
                                        );
                                    }}
                                </TableRow>)}
                            </TableBody>

                        </PTable>
                    </CardBody>
                </Card>
            </Content>
        </>
    );
}
